import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../../utils/tracking';
import { HamburgerIcon } from '../ui/HamburgerIcon';

interface NavLink {
  text: string;
  link: string;
  isActive?: boolean;
}

interface PrivatLeasingNavBarProps {
  links: NavLink[];
}

const PrivatLeasingNavBar: React.FC<PrivatLeasingNavBarProps> = ({ links }) => {
  const [activeLink, setActiveLink] = useState<string>('');
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const [navHeight, setNavHeight] = useState<number>(0);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    setNavHeight(nav.offsetHeight);
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    navRef.current?.before(sentinel);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(entry.boundingClientRect.top < 0);
      },
      {
        root: null,
        threshold: 0,
      },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      sentinel.remove();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveLink(`#${entry.target.id}`);
          }
        });
      },
      {
        rootMargin: '-70px 0px -100% 0px',
      },
    );

    const observedSections: Element[] = [];

    links.forEach((link) => {
      if (link.link.startsWith('#')) {
        const section = document.querySelector(link.link);
        if (section) {
          observer.observe(section);
          observedSections.push(section);
        }
      }
    });

    return () => {
      observedSections.forEach((section) => observer.unobserve(section));
      observer.disconnect();
    };
  }, [links]);

  const handleScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    target: string,
  ) => {
    if (target.includes('sok-aterforsaljare-verkstad')) {
      trackEvent({ event: 'find_a_dealer', model: undefined });
    }

    if (target.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(target);
      if (element) {
        const offset = 68;
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <nav
        aria-label="Hoppa till avsnitt"
        ref={navRef}
        className={`w-full bg-white z-[51] mb-5 transition-all duration-300 ${
          isFixed ? 'fixed top-0 left-0 shadow' : 'relative'
        }`}
      >
        <div className="hidden xl:flex justify-center items-center h-[59px] ">
          <ul className="flex gap-16 xl:gap-20 ">
            {links.map((link, index) => (
              <li key={index} className="relative">
                <a
                  href={link.link}
                  onClick={(e) => handleScroll(e, link.link)}
                  className={`text-base font-bold no-underline ${
                    activeLink === link.link ? 'text-gray-500' : 'text-black'
                  } hover:text-gray-500 transition`}
                >
                  {link.text}
                </a>
                {activeLink === link.link && (
                  <div className="absolute bottom-[-1px] left-0 w-full h-[2px] bg-black"></div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex xl:hidden  items-center gap-2 p-4 h-16 justify-between">
          <p className="text-sm">Innehållsförteckning privatleasing</p>
          <button
            className="relative z-30 grid place-content-center size-12"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={
              isMenuOpen
                ? 'Öppna Innehållsförteckning privatleasing'
                : 'Stäng Innehållsförteckning privatleasing'
            }
          >
            <HamburgerIcon isOpen={isMenuOpen} strokeColor="black" />
          </button>
        </div>

        {isMenuOpen && (
          <div className="absolute top-16 left-0 w-full bg-white shadow-lg z-40">
            <ul className="flex flex-col gap-4 p-4">
              {links.map((link, index) => (
                <li key={index} className="relative">
                  <a
                    href={link.link}
                    onClick={(e) => handleScroll(e, link.link)}
                    className={`block text-base font-medium no-underline ${
                      activeLink === link.link ? 'text-gray-500' : 'text-black'
                    } hover:text-gray-500 transition`}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
      {isFixed && <div className="mb-5" style={{ height: navHeight }}></div>}
    </>
  );
};

export default PrivatLeasingNavBar;
